import { useLazyQuery } from "@apollo/client";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import FileDownload from "@mui/icons-material/FileDownload";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExportDataButton } from "components/ExportDataButton";
import {
  FacilityWasteView,
  WasteStreamValidationResult,
  WasteStreamsQuery,
  WasteStreamsQueryVariables,
} from "generated-graphql/graphql";
import { ExportParams, useExportData } from "hooks/useExportData";
import { useCallback, useMemo, useState } from "react";

import { useWasteStreamInputValidation } from "./validationHooks/useWasteStreamInputValidation";
import { WasteStreamFlatFileExportModal } from "./WasteStreamFlatFileExportModal";

interface WasteStreamExportDropdownProps {
  isStaff: boolean;
  disabled?: boolean;
  facilityId: string;
  tenantId: string;
  fileName?: string;
  filter?: Record<string, unknown>;
  dataQuery: TypedDocumentNode<WasteStreamsQuery, WasteStreamsQueryVariables>;
  dataQueryVariables: WasteStreamsQueryVariables;
  onWasteStreamClick?: (wasteStreamId: string) => void;
}

/**
 * The grid's filters narrowed to reportable waste. The flat file is an EPA
 * filing, so it holds reportable waste whatever the grid is showing, matching
 * the staff report that builds the same file.
 */
function useFlatFileScope(
  filter: Record<string, unknown> | undefined,
  dataQueryVariables: WasteStreamsQueryVariables
) {
  return useMemo(
    () => ({
      flatFileFilter: { ...filter, view: FacilityWasteView.Reportable },
      flatFileQueryVariables: { ...dataQueryVariables, view: FacilityWasteView.Reportable },
    }),
    [filter, dataQueryVariables]
  );
}

export function WasteStreamExportDropdown({
  isStaff,
  disabled,
  facilityId,
  tenantId,
  fileName,
  filter,
  dataQuery,
  dataQueryVariables,
  onWasteStreamClick,
}: WasteStreamExportDropdownProps) {
  const alerts = useAlerts();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openExportWasteStreamFlatFileModal, setOpenExportWasteStreamFlatFileModal] =
    useState(false);
  const [validationResults, setValidationResults] = useState<WasteStreamValidationResult[]>([]);
  const [allWasteStreams, setAllWasteStreams] = useState<Array<{ id: string; name: string }>>([]);

  const { startExport, status } = useExportData({ autoDownload: true });
  const isLoading = status === "loading";
  const { validate: validateWasteStreams, loading: isValidating } = useWasteStreamInputValidation();
  const [fetchAllWasteStreams] = useLazyQuery<WasteStreamsQuery, WasteStreamsQueryVariables>(
    dataQuery
  );

  const { flatFileFilter, flatFileQueryVariables } = useFlatFileScope(filter, dataQueryVariables);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportWasteStreams = useCallback(() => {
    handleClose();
    const params: ExportParams = {
      exportType: "wasteStreams",
      facilityId,
      tenantId,
      fileName,
      filter,
    };
    alerts.success("Generating file...");
    startExport(params);
  }, [facilityId, tenantId, fileName, filter, alerts, startExport]);

  const handleGenerateFlatFile = useCallback(async () => {
    handleClose();

    try {
      // Fetch all waste streams using the same query and filters, but with large pageSize
      // This uses the same query document, so Apollo cache will be shared
      const { data } = await fetchAllWasteStreams({
        variables: flatFileQueryVariables,
        fetchPolicy: "cache-first", // Prefer cache from the grid's query
      });

      const wasteStreams = data?.facility?.wasteStreams?.items ?? [];
      const wasteStreamIds = wasteStreams.map((ws) => ws.id);

      // Store waste streams for the modal
      setAllWasteStreams(
        wasteStreams.map((ws) => ({
          id: ws.id,
          name: ws.name,
        }))
      );

      if (wasteStreamIds.length === 0) {
        // No waste streams to validate, proceed with export
        const params: ExportParams = {
          exportType: "rcraInfoFlatFile",
          facilityId,
          tenantId,
          filter: flatFileFilter,
        };
        alerts.success("Generating RCRAInfo flat file...");
        startExport(params);
        return;
      }

      // Validate all waste streams (pass date range so amounts align with grid/export)
      const results = await validateWasteStreams(wasteStreamIds, {
        startDate: flatFileQueryVariables.startDate ?? undefined,
        endDate: flatFileQueryVariables.endDate ?? undefined,
      });

      // Check if any waste streams have issues
      const wasteStreamsWithIssues = results.filter((result) => result.issues.length > 0);

      if (wasteStreamsWithIssues.length > 0) {
        setValidationResults(results);
        setOpenExportWasteStreamFlatFileModal(true);
      } else {
        // No issues, proceed with export
        const params: ExportParams = {
          exportType: "rcraInfoFlatFile",
          facilityId,
          tenantId,
          filter: flatFileFilter,
        };
        alerts.success("Generating RCRAInfo flat file...");
        startExport(params);
      }
    } catch (error) {
      console.error("Error validating waste streams:", error);
      alerts.error("Error validating waste streams. Proceeding with export...");
      // On error, proceed with export anyway
      const params: ExportParams = {
        exportType: "rcraInfoFlatFile",
        facilityId,
        tenantId,
        filter: flatFileFilter,
      };
      startExport(params);
    }
  }, [
    facilityId,
    tenantId,
    flatFileFilter,
    alerts,
    startExport,
    validateWasteStreams,
    fetchAllWasteStreams,
    flatFileQueryVariables,
  ]);

  const handleGenerate = useCallback(
    (excludeInvalid: boolean) => {
      setOpenExportWasteStreamFlatFileModal(false);

      // Get valid waste stream IDs (those without issues)
      const invalidWasteStreamIds = new Set(
        validationResults
          .filter((result) => result.issues.length > 0)
          .map((result) => result.wasteStreamId)
      );

      const validWasteStreamIds = allWasteStreams
        .filter((ws) => !invalidWasteStreamIds.has(ws.id))
        .map((ws) => ws.id);

      const params: ExportParams = {
        exportType: "rcraInfoFlatFile",
        facilityId,
        tenantId,
        filter: {
          ...flatFileFilter,
          // When excludeInvalid is true, only include valid waste streams
          ...(excludeInvalid ? { wasteStreamIds: validWasteStreamIds } : {}),
        },
      };
      alerts.success("Generating RCRAInfo flat file...");
      startExport(params);
    },
    [facilityId, tenantId, flatFileFilter, alerts, startExport, validationResults, allWasteStreams]
  );

  // For non-staff users, render the original ExportDataButton
  if (!isStaff) {
    return (
      <ExportDataButton
        exportType="wasteStreams"
        disabled={disabled}
        facilityId={facilityId}
        tenantId={tenantId}
        fileName={fileName}
        filter={filter}
      />
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Tooltip title="Export">
        <IconButton
          size="medium"
          color="info"
          onClick={handleClick}
          disabled={disabled}
          loading={isLoading || isValidating}
          aria-controls={open ? "export-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <FileDownload />
        </IconButton>
      </Tooltip>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <MenuItem onClick={handleExportWasteStreams}>Export Waste Streams</MenuItem>
        <MenuItem onClick={handleGenerateFlatFile} disabled={isValidating}>
          Generate RCRAInfo Flat File
        </MenuItem>
      </Menu>
      <WasteStreamFlatFileExportModal
        open={openExportWasteStreamFlatFileModal}
        onClose={() => setOpenExportWasteStreamFlatFileModal(false)}
        onGenerate={handleGenerate}
        validationResults={validationResults}
        wasteStreams={allWasteStreams}
        totalWasteStreamCount={allWasteStreams.length}
        onWasteStreamClick={(wasteStreamId) => {
          setOpenExportWasteStreamFlatFileModal(false);
          onWasteStreamClick?.(wasteStreamId);
        }}
      />
    </>
  );
}
